import { useMemo, useState } from "react";
import { RefreshCw, Search } from "lucide-react";
import AdminLayout from "@/components/layout/AdminLayout";
import {
  useAuditLog,
  formatAuditEntry,
  getActionDotColor,
} from "@/hooks/useAuditLog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const formatDateTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()} ${hh}:${mm}`;
};

const ACTION_OPTIONS: { value: string; label: string }[] = [
  { value: "all", label: "All actions" },
  { value: "suspend_company", label: "Suspend Company" },
  { value: "unsuspend_company", label: "Unsuspend Company" },
  { value: "approve_company_reactivation_request", label: "Approve Request" },
  { value: "reject_company_reactivation_request", label: "Reject Request" },
  { value: "impersonate_user", label: "Impersonate" },
];

export default function Audit() {
  const { entries, isLoading, error, refetch } = useAuditLog();
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState<string>("all");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries.filter((e) => {
      if (actionFilter !== "all" && e.action !== actionFilter) return false;
      if (q) {
        const hay = `${e.action} ${e.admin_name} ${e.admin_email}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [entries, search, actionFilter]);

  return (
    <AdminLayout title="Audit Log">
      <div className="space-y-4">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search action or admin…"
              className="pl-8 h-9 bg-slate-900 border-slate-700 text-slate-100 placeholder:text-slate-500"
            />
          </div>

          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="h-9 w-[200px] bg-slate-900 border-slate-700 text-slate-100">
              <SelectValue placeholder="Action" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
              {ACTION_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="ml-auto flex items-center gap-3">
            <span className="text-xs text-slate-400">
              Showing {filtered.length} of {entries.length} entries
            </span>
            <Button
              size="icon"
              variant="outline"
              onClick={() => refetch()}
              className="h-9 w-9 border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-slate-900 border border-slate-800 rounded-md">
          {isLoading && (
            <ul className="divide-y divide-slate-800">
              {Array.from({ length: 10 }).map((_, i) => (
                <li key={i} className="flex items-center gap-3 px-4 py-3">
                  <Skeleton className="h-2 w-2 rounded-full bg-slate-800 shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-3.5 w-2/3 bg-slate-800" />
                    <Skeleton className="h-3 w-32 bg-slate-800" />
                  </div>
                  <Skeleton className="h-3 w-32 bg-slate-800 shrink-0" />
                </li>
              ))}
            </ul>
          )}

          {!isLoading && error && (
            <div className="text-center py-12 px-4 space-y-3">
              <p className="text-sm text-red-400">{error}</p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => refetch()}
                className="border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200"
              >
                <RefreshCw className="h-3.5 w-3.5 mr-2" />
                Retry
              </Button>
            </div>
          )}

          {!isLoading && !error && filtered.length === 0 && (
            <div className="text-center py-12 text-sm text-slate-400">
              No audit log entries found.
            </div>
          )}

          {!isLoading && !error && filtered.length > 0 && (
            <ul className="divide-y divide-slate-800">
              {filtered.map((entry) => (
                <li
                  key={entry.id}
                  className="flex items-start gap-3 px-4 py-3 hover:bg-slate-800/50 transition-colors"
                >
                  <span
                    className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${getActionDotColor(entry.action)}`}
                    aria-hidden
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white break-words">
                      {formatAuditEntry(entry)}
                    </div>
                    {entry.admin_email && (
                      <div className="text-xs text-slate-400 mt-0.5">{entry.admin_email}</div>
                    )}
                  </div>
                  <div className="text-xs text-slate-400 shrink-0 text-right whitespace-nowrap">
                    {formatDateTime(entry.created_at)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
